import { FormProvider } from 'react-hook-form'
import { Fade } from 'react-awesome-reveal'
import { useTranslation } from 'next-i18next'
import React from 'react'

import { createPortal } from 'react-dom'

import Spinner from '@/components/common/SpinnerPage'
import Footer from '@/components/Footer'
import { getNavigationIcon } from '@/constants/navigationAssets'
import { useIsMobile } from '@/utils/useMobile'
import { getParkingAddress } from '@/utils/getParkingAddress'
import { getParkingLabel } from '@/utils/getParkingLabel'
import MapView from '@/components/parkings/MapView'
import CustomReveal from '@/components/common/CustomReveal'
import CommonButton from '@/components/form/Button/CommonButton'
import BtnGlobalCustomStyles from '@/components/form/Button/button.customStyles'
import FooterMobile from '@/components/FooterMobile'

import NoResults from './components/NoResults'
import Filters from './components/Filters/Filters'
import FiltersContent from './components/Filters/FiltersContent'
import NavipayAdvertisement from './components/NavipayAdvertisement/NavipayAdvertisement'
import { useSearchNearParking } from './hooks/useSearchNearParking'
import FilterButton from './components/FilterButton/FilterButton'
import {
  ContainerForResults,
  Wrapper,
  SearchWrapper,
  StyledParkingCnt,
  Overlay,
  OverlayContent,
  SpinnerOverlay
} from './SearchNearParking.styled'
import ParkingTile from './components/ParkingTile'
import ModalMap from './components/ModalMap/ModalMap'
import NoResultsSuggestions from './components/NoResultsSuggestions/NoResultsSuggestions'
import ResultSearch from './components/ResultSearch/ResultSearch'
import ParkingTileMobile from './components/ParkingTileMobile'
import SortDropdown from './components/SortDropdown/SortDropdown'

const SearchNearParking = ({
  setParkingsCount,
  initialLocation
}: {
  setParkingsCount: (val: number | string) => void
  initialLocation?: {
    lat: number
    lon: number
    label: string
  }
}) => {
  const {
    onSubmit,
    handleGoToReservation,
    form,
    parkings,
    market,
    filterOptions,
    removeFilterOption,
    clearAllFilters,
    isLoading,
    handleOverlayToggle,
    isOverlayVisible,
    isMapShow,
    onMapModalClose,
    onMapModalOptn,
    handleAddRemoveService,
    selectedServices,
    radius,
    setRadius,
    handlePriceChangeComplete,
    currency,
    latLon,
    isFixed
  } = useSearchNearParking({ setParkingsCount, initialLocation })

  const { isMobile } = useIsMobile()
  const { t } = useTranslation('parkings')
  const handler = document.getElementById('handler')

  if (!handler) return null

  return (
    <FormProvider {...form}>
      <Fade triggerOnce>
        <Wrapper
          id="search-near-parking-section"
          data-test-id="search-near-parking-section"
        >
          <form onSubmit={form.handleSubmit(data => onSubmit(data, true))}>
            {isMobile ? (
              createPortal(
                <SearchWrapper data-test-id="search-wrapper">
                  <ResultSearch
                    market={market}
                    isFixed={isFixed}
                    control={form.control}
                  />
                </SearchWrapper>,
                handler
              )
            ) : (
              <SearchWrapper data-test-id="search-wrapper">
                <ResultSearch
                  market={market}
                  isFixed={isFixed}
                  control={form.control}
                />
              </SearchWrapper>
            )}
          </form>
          {isLoading ? (
            <SpinnerOverlay>
              <Spinner />
            </SpinnerOverlay>
          ) : (
            <ContainerForResults data-test-id="container-for-results">
              {!isMobile ? (
                <Filters
                  options={filterOptions}
                  removeOption={removeFilterOption}
                  clearAllFilters={clearAllFilters}
                  onMapModalOptn={onMapModalOptn}
                  handleAddRemoveService={handleAddRemoveService}
                  selectedServices={selectedServices}
                  handlePriceChangeComplete={handlePriceChangeComplete}
                  radius={radius}
                  setRadius={setRadius}
                  currency={currency}
                  handleOverlayToggle={handleOverlayToggle}
                />
              ) : (
                <FilterButton
                  handleOverlayToggle={handleOverlayToggle}
                  onMapModalOptn={onMapModalOptn}
                />
              )}
              {parkings?.length ? (
                <StyledParkingCnt data-test-id="parking-list">
                  {!isMobile && latLon?.[0] && <SortDropdown />}
                  {parkings.map((el, i: number) => {
                    const description = getParkingAddress(el, t)
                    const name = getParkingLabel(el, t)
                    return (
                      <React.Fragment key={i}>
                        {isMobile ? (
                          <ParkingTileMobile
                            name={name}
                            description={description}
                            imageUrl={el?.mainPictureUrl}
                            parking={el}
                            onClick={e => handleGoToReservation(e, el)}
                            dataTestId={`parking-tile-mobile-${el.urlSlug}`}
                          />
                        ) : (
                          <ParkingTile
                            name={name}
                            description={description}
                            imageUrl={el?.mainPictureUrl}
                            parking={el}
                            onClick={e => handleGoToReservation(e, el)}
                            dataTestId={`parking-tile-${el.urlSlug}`}
                            onMapModalOptn={onMapModalOptn}
                          />
                        )}
                      </React.Fragment>
                    )
                  })}
                  {isMobile && market !== 'pl' && <NavipayAdvertisement />}
                </StyledParkingCnt>
              ) : (
                <div>
                  <NoResults />
                  <NoResultsSuggestions
                    latLon={latLon}
                    onMapModalOptn={onMapModalOptn}
                  />
                </div>
              )}
            </ContainerForResults>
          )}
          {isMobile ? (
            <FooterMobile data-test-id="footer" />
          ) : (
            <Footer data-test-id="footer" />
          )}
        </Wrapper>
      </Fade>
      <ModalMap onClose={onMapModalClose} show={isMapShow}>
        <CustomReveal>
          <MapView
            parkings={parkings}
            resetMargin
            zoom={13}
            city={{
              latitude: latLon?.[0] as any,
              longitude: latLon?.[1] as any
            }}
            customButtons
            data-test-id="map-view"
            userLocations={[latLon?.[0] as any, latLon?.[1] as any]}
          />
        </CustomReveal>
      </ModalMap>
      <Overlay isVisible={isMobile ? isOverlayVisible : false}>
        <OverlayContent>
          <CommonButton
            variant="close"
            onClick={handleOverlayToggle}
            customStyle={BtnGlobalCustomStyles.searchNearParking}
          >
            <img
              src={getNavigationIcon('close')}
              alt="closeIcon"
              data-test-id="close-button"
            />
          </CommonButton>
          <FiltersContent
            clearAllFilters={clearAllFilters}
            options={filterOptions}
            removeOption={removeFilterOption}
            handleAddRemoveService={handleAddRemoveService}
            selectedServices={selectedServices}
            handlePriceChangeComplete={handlePriceChangeComplete}
            radius={radius}
            setRadius={setRadius}
            currency={currency}
            handleOverlayToggle={handleOverlayToggle}
            latLon={latLon}
          />
        </OverlayContent>
      </Overlay>
    </FormProvider>
  )
}

export default SearchNearParking
